import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import Header from "@/components/main/Header";
import Footer from "@/components/main/Footer";
import type { TBlog } from "@/pages/api/admin/blog";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { useTranslation } from "react-i18next";
import Link from "next/link";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1200px;
  overflow-x: hidden;

  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow-y: scroll;
`;

const Title = styled.p`
  margin: 55px 0;
  font-family: "Lato Bold";
  font-size: 40px;
  font-weight: bold;
  color: #444;
`;

const SearchWrap = styled.div`
  width: 100%;
  height: 40px;
`;
const SearchInput = styled.input`
  padding: 8px 16px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.035);
  border-radius: 10px;
  font-size: 14px;
  border: 1px solid #000;
  color: #555;

  &:focus {
    stroke: none;
    outline: none;
  }
`;

const List = styled.div`
  margin: 0 0 0 -20px;
  width: calc(100% + 40px);
`;
const Item = styled(Link)`
  margin: 20px;
  border-radius: 5px;
  overflow: hidden;
  background-color: #fff;
`;
const ItemImg = styled.img`
  width: 100%;
`;
const ItemContent = styled.div`
  padding: 30px;
`;
const ItemCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const ItemCategory = styled.p`
  margin: 0 10px 10px 0;
  font-family: "Hind";
  font-size: 14px;
  color: #971b2f;
`;
const ItemTitle = styled.p`
  margin: 20px 0;
  font-family: "Lato Bold", sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: #000;
`;
const ItemDesc = styled.p`
  line-height: 26px;
  font-family: "Hind";
  font-size: 16px;
  color: #000;
`;

export default function Page() {
  const { t } = useTranslation();

  const footerRef = useRef<HTMLDivElement>(null);

  const [searchInput, setSearchInput] = useState<string>("");
  const [blogItems, setBlogItems] = useState<TBlog[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);

    return () => {
      if (footerRef.current) observer.unobserve(footerRef.current);
    };
  }, [hasMore]);

  useEffect(() => {
    async function init() {
      const newBlogItems = await fetchBlogItems(page, 10);
      if (newBlogItems.length < 1) return setHasMore(false);

      setBlogItems((prev) => {
        for (const i in newBlogItems) {
          if (prev.findIndex((v) => v.id === newBlogItems[i].id) > -1) continue;

          prev.push(newBlogItems[i]);
        }

        return [...prev];
      });
    }
    if (hasMore) init();
  }, [page, hasMore]);

  return (
    <>
      <Header />
      <Wrap>
        <Title>{t("learn.blog.title")}</Title>
        <SearchWrap>
          <SearchInput
            type="text"
            placeholder={t("learn.blog.search")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
          />
        </SearchWrap>
        <List>
          <ResponsiveMasonry columnsCountBreakPoints={{ 367: 1, 800: 2, 1000: 3 }}>
            <Masonry>
              {blogItems.map((v, i) => (
                <Item
                  key={i}
                  href={`/learn/blog/${v.id}`}
                  data-aos="fade-up"
                  data-aos-easing="ease-out"
                  data-aos-duration="500"
                >
                  <ItemImg src={v.bg} />
                  <ItemContent>
                    {/* <ItemCategories>
                      {v.categories.map((vv, j) => (
                        <ItemCategory key={j}>{vv}</ItemCategory>
                      ))}
                    </ItemCategories> */}
                    <ItemCategories>
                      <ItemCategory>{dayjs(v.created_date).format("MMMM D, YYYY")}</ItemCategory>
                    </ItemCategories>
                    <ItemTitle>{v.title}</ItemTitle>
                    <ItemDesc>{v.content}...</ItemDesc>
                  </ItemContent>
                </Item>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </List>
      </Wrap>
      <div ref={footerRef}>
        <Footer />
      </div>
    </>
  );
}

async function fetchBlogItems(page: number = 0, limit: number = 20): Promise<TBlog[]> {
  const fetchResult = await fetch("/api/blog/list", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ page, limit }),
  });

  const result = await fetchResult.json();
  return result.result;
}
